import React, { useState, useEffect } from 'react';
import { BILLING_BRAND } from '../data/billingBrandConfig';

// Nombre de marca por app (Impulza / Gastify). billingBrandConfig esta
// PROTECTED en el sync, asi que cada app conserva su brandName sin
// hardcodear "Impulza" aca.
const BRAND_NAME = BILLING_BRAND?.brandName || 'el sistema';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { buildLocalDateRangeTimestamp } from '../utils/localDateTime';
import {
  Landmark,
  Upload,
  CheckCircle2,
  AlertCircle,
  Plus,
  RefreshCcw,
  CheckSquare,
  CalendarRange,
  ArrowLeftRight,
  FileSpreadsheet
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function ConciliacionBancaria() {
  const { profile } = useAuth();
  const [bancos, setBancos] = useState([]);
  const [bancoSeleccionado, setBancoSeleccionado] = useState('');
  
  const [fechaDesde, setFechaDesde] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [fechaHasta, setFechaHasta] = useState(() => new Date().toISOString().split('T')[0]);

  // Movimientos del sistema
  const [movimientosSistema, setMovimientosSistema] = useState([]);
  const [loadingSistema, setLoadingSistema] = useState(false);

  // Movimientos del banco (Importados CSV)
  const [movimientosBanco, setMovimientosBanco] = useState([]);
  
  // Para minutas
  const [showMinutaModal, setShowMinutaModal] = useState(false);
  const [minutaItem, setMinutaItem] = useState(null);

  useEffect(() => {
    if (profile?.organization_id) {
      cargarBancos();
    }
  }, [profile]);

  useEffect(() => {
    if (bancoSeleccionado) {
      cargarMovimientosSistema();
    }
  }, [bancoSeleccionado, fechaDesde, fechaHasta]);

  const cargarBancos = async () => {
    try {
      const { data, error } = await supabase
        .from('banks')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .order('name', { ascending: true });
      if (error) throw error;
      setBancos(data || []);
      if (data?.length > 0) setBancoSeleccionado(data[0].id);
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar bancos");
    }
  };

  const cargarMovimientosSistema = async () => {
    setLoadingSistema(true);
    try {
      // Rango en hora LOCAL (AR), no Z falso: con sufijo Z el "día" cortaba a las
      // 21:00 ART y dejaba afuera movimientos de 21–24 h. Bug T6.
      const start = buildLocalDateRangeTimestamp(fechaDesde, false);
      const end = buildLocalDateRangeTimestamp(fechaHasta, true);

      const { data, error } = await supabase
        .from('movimientos_financieros')
        .select('*')
        .eq('organization_id', profile?.organization_id) // scope de org (era la única query del módulo sin filtrar). Bug T6.
        .eq('bank_id', bancoSeleccionado)
        .gte('fecha_operacion', start)
        .lte('fecha_operacion', end)
        .order('fecha_operacion', { ascending: false });
        
      if (error) throw error;
      setMovimientosSistema(data || []);
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar movimientos de Tesorería.");
    } finally {
      setLoadingSistema(false);
    }
  };

  const toggleConciliado = async (movId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('movimientos_financieros')
        .update({
            conciliado: !currentStatus,
            fecha_conciliado: !currentStatus ? new Date().toISOString() : null
        })
        .eq('id', movId)
        .eq('organization_id', profile?.organization_id); // scope de org. Bug T6.
        
      if (error) throw error;
      
      setMovimientosSistema(prev => 
        prev.map(m => m.id === movId ? { ...m, conciliado: !currentStatus } : m)
      );
      toast.success(!currentStatus ? "Marcado como conciliado" : "Conciliación revertida");
    } catch (err) {
      console.error(err);
      toast.error("Ocurrió un error (¿Ejecutaste el script SQL?)");
    }
  };

  // LECTURA DE CSV BASICA (Demo)
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n');
      
      const parsed = [];
      // Asume formato: Fecha,Descripcion,Referencia,Monto
      // Saltamos la cabecera i=1
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        const parts = line.split(',');
        if (parts.length >= 4) {
             parsed.push({
                 id: `ext_${i}`,
                 fecha: parts[0],
                 descripcion: parts[1],
                 referencia: parts[2],
                 monto: Number(parts[3] || 0),
                 conciliado: false
             });
        }
      }
      setMovimientosBanco(parsed);
      toast.success(`${parsed.length} movimientos de extracto importados.`);
    };
    reader.readAsText(file);
  };

  const marcarBancoConciliado = (idExt) => {
      setMovimientosBanco(prev => 
        prev.map(m => m.id === idExt ? { ...m, conciliado: !m.conciliado } : m)
      );
  };

  const abrirMinuta = (itemExt) => {
      setMinutaItem(itemExt);
      setShowMinutaModal(true);
  };

  const crearMinuta = async () => {
      if(!minutaItem) return;
      try {
          const isIngreso = minutaItem.monto > 0;
          const { error } = await supabase.from('movimientos_financieros').insert([{
              organization_id: profile.organization_id,
              bank_id: bancoSeleccionado,
              tipo: isIngreso ? 'Ingreso' : 'Egreso',
              categoria: 'Minuta/Ajuste',
              monto: Math.abs(minutaItem.monto),
              fecha_operacion: new Date().toISOString(),
              descripcion: minutaItem.descripcion || 'Minuta de Conciliación',
              documento_tipo: 'Minuta',
              conciliado: true,
              fecha_conciliado: new Date().toISOString()
          }]);
          if (error) throw error;
          
          toast.success("Minuta registrada e integrada.");
          setShowMinutaModal(false);
          marcarBancoConciliado(minutaItem.id);
          cargarMovimientosSistema(); // Refrescar lado izquierdo
      } catch (err) {
          console.error(err);
          toast.error("Error creando minuta.");
      }
  };

  const pendientesSistema = movimientosSistema.filter(m => !m.conciliado).length;
  const pendientesExtracto = movimientosBanco.filter(m => !m.conciliado).length;
  const conciliadosSistema = movimientosSistema.filter(m => m.conciliado).length;
  const bancoActual = bancos.find(b => b.id === bancoSeleccionado);

  return (
    <div className="p-3 sm:p-4 lg:p-6 max-w-[1600px] w-full mx-auto space-y-6 animate-in fade-in h-full flex flex-col pt-16 lg:pt-6 bg-[#F8FAFC]">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-800">
            <RefreshCcw className="text-indigo-600" />
            Conciliación Bancaria
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Cruza la información contable de {BRAND_NAME} contra tus extractos bancarios.
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-white p-3 rounded-2xl border border-slate-200 shadow-sm w-full md:w-auto">
          <select 
            title="Seleccionar cuenta bancaria a conciliar"
            value={bancoSeleccionado}
            onChange={e => setBancoSeleccionado(e.target.value)}
            className="text-[10px] font-black uppercase tracking-tight bg-slate-50 border border-slate-100 rounded-xl px-3 py-2.5 outline-none text-slate-700"
          >
            <option value="">Seleccionar Banco...</option>
            {bancos.map(b => (
              <option key={b.id} value={b.id}>{b.name} - {b.currency || 'ARS'}</option>
            ))}
          </select>
          <input 
            title="Fecha inicial de conciliacion"
            type="date" 
            value={fechaDesde}
            onChange={e => setFechaDesde(e.target.value)}
            className="text-[10px] font-black uppercase tracking-tight bg-slate-50 border border-slate-100 rounded-xl px-3 py-2.5 outline-none text-slate-700 cursor-pointer"
          />
          <input 
            title="Fecha final de conciliacion"
            type="date" 
            value={fechaHasta}
            onChange={e => setFechaHasta(e.target.value)}
            className="text-[10px] font-black uppercase tracking-tight bg-slate-50 border border-slate-100 rounded-xl px-3 py-2.5 outline-none text-slate-700 cursor-pointer"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 shrink-0">
        <div title="Cuenta seleccionada para la conciliacion actual" className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Banco activo</p>
            <Landmark size={16} className="text-[#51A7FD]" />
          </div>
          <p className="mt-1.5 text-[18px] font-black text-slate-800 tracking-tight uppercase break-words">
            {bancoActual?.name || 'Sin banco'}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            {bancoActual?.currency || 'ARS'}
          </p>
        </div>
        <div title="Movimientos de tesoreria todavia no conciliados" className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Pendientes sistema</p>
            <ArrowLeftRight size={16} className="text-amber-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{pendientesSistema}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{BRAND_NAME}</p>
        </div>
        <div title="Movimientos importados sin marcar en el extracto" className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Pendientes extracto</p>
            <FileSpreadsheet size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{pendientesExtracto}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">CSV importado</p>
        </div>
        <div title="Movimientos del sistema ya conciliados en el periodo" className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Conciliados</p>
            <CheckCircle2 size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{conciliadosSistema}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Sistema</p>
        </div>
      </div>

      {/* DUAL PANE */}
      <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0">
          
          {/* LADO IZQUIERDO: SISTEMA */}
          <div className="flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden min-h-[420px]">
              <div className="px-5 py-4 border-b border-white/10 bg-[#0B2A36] flex justify-between items-center gap-3">
                  <h2 className="text-[11px] font-black text-white uppercase tracking-widest flex items-center gap-2">
                     <Landmark size={18} className="text-[#3FF2AA]"/> 
                     Movimientos en {BRAND_NAME}
                  </h2>
                  <span title="Cantidad de movimientos del sistema aun pendientes de conciliar" className="text-[9px] bg-white/10 text-white px-2.5 py-1 rounded-lg font-black uppercase tracking-widest">
                      {pendientesSistema} pdtes.
                  </span>
              </div>
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/70">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                  <CalendarRange size={12} className="text-slate-300" />
                  Periodo {fechaDesde} al {fechaHasta}
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {loadingSistema ? (
                      <div className="p-10 text-center text-slate-400">Cargando...</div>
                  ) : movimientosSistema.length === 0 ? (
                      <div className="p-10 text-center text-slate-400">No hay movimientos en este periodo.</div>
                  ) : (
                      movimientosSistema.map(m => {
                          const isIngreso = String(m.tipo).toLowerCase().includes('ingreso');
                          const montoNum = m.monto || 0;
                          
                          return (
                              <div key={m.id} className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${m.conciliado ? 'bg-emerald-50/50 border-emerald-200' : 'bg-white border-slate-200 hover:border-indigo-300'}`}>
                                  <div className="flex items-center gap-3">
                                      <button 
                                        onClick={() => toggleConciliado(m.id, m.conciliado)}
                                        title="Marcar o revertir conciliacion en el sistema"
                                        className={`w-6 h-6 rounded flex items-center justify-center border transition-colors ${m.conciliado ? 'bg-emerald-500 border-emerald-600 text-white' : 'bg-slate-50 border-slate-300 text-transparent hover:border-emerald-500'}`}
                                      >
                                          <CheckSquare size={14} />
                                      </button>
                                      <div>
                                          <div className="text-sm font-bold text-slate-800">{m.descripcion || m.documento_tipo || m.categoria}</div>
                                          <div className="text-xs text-slate-500">{new Date(m.fecha_operacion || m.created_at).toLocaleDateString()} • {m.categoria || 'S/C'}</div>
                                      </div>
                                  </div>
                                  <div className={`text-sm font-bold ${isIngreso ? 'text-emerald-600' : 'text-slate-700'}`}>
                                      {isIngreso ? '+' : '-'}${Math.abs(montoNum).toLocaleString('es-AR', {minimumFractionDigits: 2})}
                                  </div>
                              </div>
                          );
                      })
                  )}
              </div>
          </div>

          {/* LADO DERECHO: EXTRACTO BANCARIO */}
          <div className="flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden min-h-[420px]">
              <div className="px-5 py-4 border-b border-white/10 bg-[#0B2A36] flex justify-between items-center gap-3">
                  <h2 className="text-[11px] font-black text-white uppercase tracking-widest flex items-center gap-2">
                     <Upload size={18} className="text-[#3FF2AA]"/> 
                     Extracto Importado
                  </h2>
                  <label title="Importar extracto bancario en CSV o TXT" className="cursor-pointer bg-white/10 border border-white/10 px-3 py-1.5 rounded-lg text-[10px] font-black text-white hover:bg-white/20 transition-colors shadow-sm flex items-center gap-2 uppercase tracking-widest">
                      <Upload size={14}/> Subir CSV
                      <input type="file" accept=".csv, .txt" className="hidden" onChange={handleFileUpload} />
                  </label>
              </div>
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/70">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                  {movimientosBanco.length} registros importados
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {movimientosBanco.length === 0 ? (
                      <div className="p-10 text-center flex flex-col items-center justify-center text-slate-400 border-2 border-dashed border-slate-100 rounded-xl m-2 h-[80%]">
                          <Upload className="mb-2 opacity-30" size={32}/>
                          <p className="text-sm">Importa el extracto de tu banco en formato CSV.</p>
                          <p className="text-xs mt-1 max-w-[250px] opacity-70">El archivo debe contener columnas: Fecha, Descripcion, Referencia, Monto.</p>
                      </div>
                  ) : (
                      movimientosBanco.map(m => {
                          const isIngreso = m.monto > 0;
                          return (
                              <div key={m.id} className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${m.conciliado ? 'bg-slate-100 border-slate-200 opacity-60 grayscale' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                                  <div className="flex items-center gap-3">
                                      <button 
                                        onClick={() => marcarBancoConciliado(m.id)}
                                        title="Marcar o revertir conciliacion del extracto importado"
                                        className={`w-6 h-6 rounded-full flex items-center justify-center border transition-colors ${m.conciliado ? 'bg-slate-400 border-slate-500 text-white' : 'bg-slate-50 border-slate-300 text-transparent hover:border-slate-400'}`}
                                      >
                                          <CheckCircle2 size={14} />
                                      </button>
                                      <div>
                                          <div className="text-sm font-bold text-slate-800">{m.descripcion}</div>
                                          <div className="text-xs text-slate-500">{m.fecha} • {m.referencia}</div>
                                      </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                      <div className={`text-sm font-bold ${isIngreso ? 'text-emerald-600' : 'text-slate-700'}`}>
                                          {isIngreso ? '+' : ''}${Math.abs(m.monto).toLocaleString('es-AR', {minimumFractionDigits: 2})}
                                      </div>
                                      
                                      {/* BOTON MINUTA (Añadir al sistema directo) */}
                                      {!m.conciliado && (
                                          <button 
                                            onClick={() => abrirMinuta(m)}
                                            title="Crear Minuta en el Sistema"
                                            className="w-7 h-7 rounded border border-indigo-200 text-indigo-600 bg-indigo-50 flex items-center justify-center hover:bg-indigo-600 hover:text-white transition-colors"
                                          >
                                              <Plus size={14} />
                                          </button>
                                      )}
                                  </div>
                              </div>
                          );
                      })
                  )}
              </div>
          </div>
      </div>

      {/* MODAL MINUTA */}
      {showMinutaModal && minutaItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95">
                <div className="p-6">
                    <h3 className="text-lg font-bold text-slate-800 mb-2">Crear Minuta desde Extracto</h3>
                    <p className="text-sm text-slate-500 mb-4">Se creará un movimiento en {BRAND_NAME} y se marcará como conciliado automáticamente.</p>
                    
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2 mb-6">
                        <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Concepto:</span>
                            <span className="font-bold">{minutaItem.descripcion}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Monto:</span>
                            <span className={`font-bold ${minutaItem.monto > 0 ? 'text-emerald-600' : 'text-slate-700'}`}>
                                ${Math.abs(minutaItem.monto).toLocaleString('es-AR', {minimumFractionDigits:2})}
                            </span>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <button 
                          onClick={() => setShowMinutaModal(false)}
                          title="Cerrar sin registrar la minuta"
                          className="flex-1 px-4 py-2 border border-slate-200 rounded-xl font-medium text-slate-600 hover:bg-slate-50"
                        >
                            Cancelar
                        </button>
                        <button 
                          onClick={crearMinuta}
                          title={`Crear el movimiento en ${BRAND_NAME} y marcarlo como conciliado`}
                          className="flex-1 px-4 py-2 bg-[#0B2A36] hover:bg-slate-800 text-white rounded-xl font-medium shadow-sm"
                        >
                            Registrar Minuta
                        </button>
                    </div>
                </div>
            </div>
        </div>
      )}
    </div>
  );
}
